import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Snowflake, Search, Filter, ArrowLeft, Clock, Users, Database,
  DollarSign, AlertTriangle, TrendingUp, TrendingDown, Copy, 
  Lightbulb, Zap, RefreshCw, Download, ChevronDown, ChevronUp,
  Play, XCircle, CheckCircle, Eye, Code
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import BrandTitle from '../components/BrandTitle';

const SnowflakeQueryExplorer = () => {
  const { shouldShowData } = useDemoData();
  // Iter84al — ping real backend on mount; auth-validated. Local rich
  // hardcoded query data still drives the UI rendering.
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/finops/v2/snowflake-query/explorer`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [costFilter, setCostFilter] = useState('all');
  const [sortBy, setSortBy] = useState('cost');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [showOptimizations, setShowOptimizations] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => {
      setRefreshing(false);
      toast.success('Query data refreshed');
    }, 1500);
  };

  // Mock data - Comprehensive query list
  const allQueries = [
    {
      id: 'Q-7834',
      queryText: `SELECT 
  p.product_category,
  d.region,
  SUM(f.sales_amount) as total_sales,
  COUNT(DISTINCT f.customer_id) as unique_customers,
  AVG(f.order_value) as avg_order_value
FROM fact_sales f
JOIN dim_products p ON f.product_id = p.product_id
JOIN dim_geography d ON f.geo_id = d.geo_id
WHERE f.sale_date >= DATEADD(month, -12, CURRENT_DATE())
GROUP BY 1, 2
ORDER BY total_sales DESC`,
      user: 'john.smith@company.com',
      role: 'ANALYST',
      warehouse: 'ANALYTICS_WH',
      warehouseSize: 'X-Large',
      credits: 234.5,
      cost: 586.25,
      duration: '45m 23s',
      durationMs: 2723000,
      rowsScanned: '2.3B',
      rowsReturned: '1,234',
      bytesScanned: '847 GB',
      startTime: '2026-03-05 03:45:12',
      endTime: '2026-03-05 04:30:35',
      status: 'completed',
      optimizations: [
        { type: 'clustering', title: 'Add Clustering Key', description: 'Cluster fact_sales by sale_date to reduce scan time by ~60%', savings: '~$350/query' },
        { type: 'filter', title: 'Push Down Filters', description: 'Move date filter to subquery for earlier pruning', savings: '~$120/query' },
      ]
    },
    {
      id: 'Q-7821',
      queryText: `CREATE TABLE analytics.monthly_report AS
SELECT 
  DATE_TRUNC('month', order_date) as month,
  customer_segment,
  COUNT(*) as order_count,
  SUM(amount) as revenue
FROM orders
JOIN customers USING (customer_id)
GROUP BY 1, 2`,
      user: 'data_team@company.com',
      role: 'DATA_ENGINEER',
      warehouse: 'ETL_WH',
      warehouseSize: 'Large',
      credits: 187.2,
      cost: 468.00,
      duration: '32m 11s',
      durationMs: 1931000,
      rowsScanned: '890M',
      rowsReturned: '2,456',
      bytesScanned: '234 GB',
      startTime: '2026-03-05 02:00:00',
      endTime: '2026-03-05 02:32:11',
      status: 'completed',
      optimizations: [
        { type: 'incremental', title: 'Use Incremental Load', description: 'Switch to incremental processing instead of full table rebuild', savings: '~$400/run' },
      ]
    },
    {
      id: 'Q-7819',
      queryText: `INSERT INTO warehouse.aggregated_metrics
SELECT 
  metric_date,
  metric_name,
  SUM(metric_value) as total_value,
  COUNT(*) as data_points
FROM raw_metrics
WHERE metric_date >= CURRENT_DATE() - 7
GROUP BY 1, 2`,
      user: 'etl_pipeline',
      role: 'SERVICE_ACCOUNT',
      warehouse: 'ETL_WH',
      warehouseSize: 'Large',
      credits: 156.8,
      cost: 392.00,
      duration: '28m 45s',
      durationMs: 1725000,
      rowsScanned: '1.2B',
      rowsReturned: '45,678',
      bytesScanned: '456 GB',
      startTime: '2026-03-05 01:00:00',
      endTime: '2026-03-05 01:28:45',
      status: 'completed',
      optimizations: []
    },
    {
      id: 'Q-7815',
      queryText: `SELECT * FROM customers WHERE customer_id = 12345`,
      user: 'jane.doe@company.com',
      role: 'ANALYST',
      warehouse: 'ANALYTICS_WH',
      warehouseSize: 'X-Large',
      credits: 0.8,
      cost: 2.00,
      duration: '1.2s',
      durationMs: 1200,
      rowsScanned: '50M',
      rowsReturned: '1',
      bytesScanned: '2.3 GB',
      startTime: '2026-03-05 10:15:00',
      endTime: '2026-03-05 10:15:01',
      status: 'completed',
      optimizations: [
        { type: 'warehouse', title: 'Use Smaller Warehouse', description: 'Simple lookup queries don\'t need X-Large warehouse', savings: '~$1.50/query' },
      ]
    },
    {
      id: 'Q-7810',
      queryText: `SELECT 
  user_id,
  session_id,
  event_type,
  event_timestamp,
  page_url,
  JSON_EXTRACT_PATH_TEXT(properties, 'button_clicked') as button
FROM events
WHERE event_date = CURRENT_DATE()
AND event_type IN ('click', 'pageview', 'conversion')`,
      user: 'analytics_svc',
      role: 'SERVICE_ACCOUNT',
      warehouse: 'REPORTING_WH',
      warehouseSize: 'Medium',
      credits: 45.6,
      cost: 114.00,
      duration: '8m 34s',
      durationMs: 514000,
      rowsScanned: '234M',
      rowsReturned: '1.2M',
      bytesScanned: '89 GB',
      startTime: '2026-03-05 09:00:00',
      endTime: '2026-03-05 09:08:34',
      status: 'completed',
      optimizations: []
    },
    {
      id: 'Q-7805',
      queryText: `CREATE OR REPLACE TABLE ml_features AS
SELECT 
  customer_id,
  AVG(order_value) as avg_order_value,
  COUNT(*) as order_count,
  DATEDIFF(day, MIN(order_date), MAX(order_date)) as customer_lifetime,
  SUM(CASE WHEN returned = TRUE THEN 1 ELSE 0 END) as return_count
FROM orders
GROUP BY 1`,
      user: 'data_team@company.com',
      role: 'DATA_ENGINEER',
      warehouse: 'DATA_SCIENCE_WH',
      warehouseSize: 'X-Large',
      credits: 89.3,
      cost: 223.25,
      duration: '15m 22s',
      durationMs: 922000,
      rowsScanned: '567M',
      rowsReturned: '2.3M',
      bytesScanned: '123 GB',
      startTime: '2026-03-04 22:00:00',
      endTime: '2026-03-04 22:15:22',
      status: 'completed',
      optimizations: [
        { type: 'scheduling', title: 'Schedule Off-Peak', description: 'Run during off-peak hours for lower credit rates', savings: '~$50/run' },
      ]
    },
    {
      id: 'Q-7798',
      queryText: `SELECT COUNT(*) FROM large_table WHERE created_at > '2025-01-01'`,
      user: 'dev_user@company.com',
      role: 'DEVELOPER',
      warehouse: 'DEV_WH',
      warehouseSize: 'Small',
      credits: 12.4,
      cost: 31.00,
      duration: '4m 12s',
      durationMs: 252000,
      rowsScanned: '2.1B',
      rowsReturned: '1',
      bytesScanned: '567 GB',
      startTime: '2026-03-05 11:30:00',
      endTime: '2026-03-05 11:34:12',
      status: 'completed',
      optimizations: [
        { type: 'approx', title: 'Use APPROX_COUNT_DISTINCT', description: 'For estimates, use approximate functions to save compute', savings: '~$25/query' },
      ]
    },
    {
      id: 'Q-7792',
      queryText: `COPY INTO @my_stage/exports/
FROM (
  SELECT * FROM reporting.daily_summary
  WHERE report_date >= '2026-01-01'
)
FILE_FORMAT = (TYPE = 'CSV')`,
      user: 'export_job',
      role: 'SERVICE_ACCOUNT',
      warehouse: 'ETL_WH',
      warehouseSize: 'Large',
      credits: 34.2,
      cost: 85.50,
      duration: '6m 45s',
      durationMs: 405000,
      rowsScanned: '45M',
      rowsReturned: '45M',
      bytesScanned: '12 GB',
      startTime: '2026-03-05 06:00:00',
      endTime: '2026-03-05 06:06:45',
      status: 'completed',
      optimizations: []
    },
  ];

  const warehouses = ['all', 'ANALYTICS_WH', 'ETL_WH', 'REPORTING_WH', 'DATA_SCIENCE_WH', 'DEV_WH'];
  const users = ['all', ...new Set(allQueries.map(q => q.user))];

  // Filter and sort queries
  const filteredQueries = useMemo(() => {
    let result = [...allQueries];

    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      result = result.filter(q => 
        q.queryText.toLowerCase().includes(search) ||
        q.user.toLowerCase().includes(search) ||
        q.id.toLowerCase().includes(search)
      );
    }

    // Warehouse filter
    if (warehouseFilter !== 'all') {
      result = result.filter(q => q.warehouse === warehouseFilter);
    }

    // User filter
    if (userFilter !== 'all') {
      result = result.filter(q => q.user === userFilter);
    }

    // Cost filter
    if (costFilter !== 'all') {
      switch (costFilter) {
        case 'high':
          result = result.filter(q => q.cost >= 100);
          break;
        case 'medium':
          result = result.filter(q => q.cost >= 10 && q.cost < 100);
          break;
        case 'low':
          result = result.filter(q => q.cost < 10);
          break;
      }
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'cost':
          aVal = a.cost;
          bVal = b.cost;
          break;
        case 'duration':
          aVal = a.durationMs;
          bVal = b.durationMs;
          break;
        case 'credits':
          aVal = a.credits;
          bVal = b.credits;
          break;
        case 'time':
          aVal = new Date(a.startTime).getTime();
          bVal = new Date(b.startTime).getTime();
          break;
        default:
          aVal = a.cost;
          bVal = b.cost;
      }
      return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

    return result;
  }, [searchQuery, warehouseFilter, userFilter, costFilter, sortBy, sortOrder]);

  const totalCost = useMemo(() => filteredQueries.reduce((sum, q) => sum + q.cost, 0), [filteredQueries]);
  const totalCredits = useMemo(() => filteredQueries.reduce((sum, q) => sum + q.credits, 0), [filteredQueries]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Query copied to clipboard');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'running':
        return <Badge className="bg-emerald-100 text-emerald-700"><Play className="w-3 h-3 mr-1" />Running</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Snowflake Query Explorer" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="space-y-6" data-testid="snowflake-query-explorer">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Search className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Query Cost Explorer</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Find and analyze expensive Snowflake queries</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Queries Found</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{filteredQueries.length}</p>
              </div>
              <Database className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Cost</p>
                <p className="text-2xl font-bold text-amber-600">${totalCost.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Total Credits</p>
                <p className="text-2xl font-bold text-emerald-600">{totalCredits.toFixed(1)}</p>
              </div>
              <Snowflake className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Optimizable</p>
                <p className="text-2xl font-bold text-emerald-600">{filteredQueries.filter(q => q.optimizations.length > 0).length}</p>
              </div>
              <Lightbulb className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search queries, users, or IDs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Warehouse" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map(wh => (
                  <SelectItem key={wh} value={wh}>
                    {wh === 'all' ? 'All Warehouses' : wh}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="User" />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user} value={user}>
                    {user === 'all' ? 'All Users' : user}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={costFilter} onValueChange={setCostFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Cost Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Costs</SelectItem>
                <SelectItem value="high">High ($100+)</SelectItem>
                <SelectItem value="medium">Medium ($10-$100)</SelectItem>
                <SelectItem value="low">Low (&lt;$10)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cost">Cost</SelectItem>
                <SelectItem value="credits">Credits</SelectItem>
                <SelectItem value="duration">Duration</SelectItem>
                <SelectItem value="time">Time</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            >
              {sortOrder === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Query List */}
      <div className="space-y-3">
        {filteredQueries.map((query) => (
          <Card 
            key={query.id} 
            className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedQuery(query)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono text-xs">{query.id}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-cyan-900/30 dark:text-cyan-400 text-xs">
                      {query.warehouse}
                    </Badge>
                    {getStatusBadge(query.status)}
                    {query.optimizations.length > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        <Lightbulb className="w-3 h-3 mr-1" />
                        {query.optimizations.length} optimization{query.optimizations.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 font-mono truncate mb-2">
                    {query.queryText.split('\n')[0]}...
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {query.user}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {query.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {query.rowsScanned} rows scanned
                    </span>
                    <span className="flex items-center gap-1">
                      <Snowflake className="w-3 h-3" />
                      {query.credits.toFixed(1)} credits
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-2xl font-bold text-amber-600">${query.cost.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">{query.startTime}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredQueries.length === 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No queries found</h3>
            <p className="text-slate-400">Try adjusting your filters or search terms</p>
          </CardContent>
        </Card>
      )}

      {/* Query Detail Modal */}
      <Dialog open={!!selectedQuery} onOpenChange={() => setSelectedQuery(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedQuery && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-emerald-500" />
                  Query Details - {selectedQuery.id}
                </DialogTitle>
                <DialogDescription>
                  Executed by {selectedQuery.user} on {selectedQuery.warehouse}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Cost Overview */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
                    <p className="text-sm text-amber-600 dark:text-amber-400">Cost</p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">${selectedQuery.cost.toFixed(2)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-cyan-900/20 text-center">
                    <p className="text-sm text-emerald-600 dark:text-cyan-400">Credits</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-cyan-300">{selectedQuery.credits.toFixed(1)}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-cyan-900/20 text-center">
                    <p className="text-sm text-emerald-600 dark:text-cyan-400">Duration</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-cyan-300">{selectedQuery.duration}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center">
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">Rows Scanned</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{selectedQuery.rowsScanned}</p>
                  </div>
                </div>

                {/* Query Text */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-slate-900 dark:text-white">Query Text</h4>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedQuery.queryText)}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy
                    </Button>
                  </div>
                  <pre className="p-4 rounded-lg bg-slate-900 text-slate-100 text-sm overflow-x-auto font-mono">
                    {selectedQuery.queryText}
                  </pre>
                </div>

                {/* Execution Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Execution Info</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Warehouse</span>
                        <span className="font-medium">{selectedQuery.warehouse} ({selectedQuery.warehouseSize})</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Start Time</span>
                        <span className="font-medium">{selectedQuery.startTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">End Time</span>
                        <span className="font-medium">{selectedQuery.endTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Bytes Scanned</span>
                        <span className="font-medium">{selectedQuery.bytesScanned}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-3">User Info</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">User</span>
                        <span className="font-medium">{selectedQuery.user}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Role</span>
                        <span className="font-medium">{selectedQuery.role}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Rows Returned</span>
                        <span className="font-medium">{selectedQuery.rowsReturned}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Status</span>
                        {getStatusBadge(selectedQuery.status)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Optimizations */}
                {selectedQuery.optimizations.length > 0 && (
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5" />
                      Optimization Recommendations
                    </h4>
                    <div className="space-y-3">
                      {selectedQuery.optimizations.map((opt, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-700">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-slate-900 dark:text-white">{opt.title}</span>
                            <Badge className="bg-emerald-100 text-emerald-700">{opt.savings}</Badge>
                          </div>
                          <p className="text-sm text-slate-400 dark:text-slate-400">{opt.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SnowflakeQueryExplorer;
